import Button from '@components/Button'
import Prompt from '@components/Prompt'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { TokenCard } from '@modules/Swap/ExchangeView'
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { logger } from '@plug-core/logger'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import React, { PropsWithChildren, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiChevronDown } from 'react-icons/hi'
import { useNavigate } from 'react-router-dom'
import BottomSheet from '@components/BottomSheet'
import useBridgeOneSec from '@plug-core/hooks/useBridgeOneSec'
import { Chain } from '@plug-core/interfaces/common'
import ReviewMint from '@modules/MintCK/ReviewMint'
import ChainPicker from '@components/ChainPicker'
import { ETH_SUPPORTED_CHAINS } from '@plug-core/utils/eth'
import Touchable from '@components/Touchable'
import { getChainIcon } from '@plug-core/utils/getChainIcon'
import { MdHistory } from 'react-icons/md'
import { ROUTES } from '@utils/routes'

const BridgeListItem = ({
  attribute,
  children,
}: PropsWithChildren & {
  attribute: string
}) => (
  <ListItem
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      my: 1,
    }}
  >
    <Typography variant="body2">{attribute}</Typography>
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      spacing={0.5}
      color="text.secondary"
    >
      {children}
    </Stack>
  </ListItem>
)

const BridgeOneSec = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [chainSelectVisible, setChainSelectVisible] = useState(false)
  const [prompt, setPrompt] = useState<{
    title: string
    description: string
  } | null>(null)

  const {
    amount,
    setAmount,
    maxAmount,
    error,
    bridgeInProgress,
    performBridge,
    bridgeMode,
    setBridgeMode,
    token,
    setToken,
    receiveAmount,
    success,
    fee,
    ethBalance,
    bridgeStep,
    bridgeReviewVisible,
    setBridgeReviewVisible,
    toToken,
    toChain,
    setToChain,
    fromTokens,
  } = useBridgeOneSec({ tokenId: undefined })

  const bridge = async () => {
    try {
      await performBridge()
      setTimeout(() => navigate(-1), 3000)
    } catch (e) {
      setBridgeReviewVisible(false)
      logger.error('Failed to bridge onesec', { originalError: e, bridgeMode })
      setPrompt({
        title: t(`onesec.errors.bridgeFailed`),
        description: t(`onesec.errors.bridgeFailedDescription`, { error: e }),
      })
    }
  }

  return (
    <Stack pt={2} pb={1}>
      <Titlebar
        title={t('onesec.title')}
        onActionClick={{
          type: 'back',
          action: () => navigate(-1),
        }}
        leftItem={
          <IconButton
            sx={{ width: 40, marginRight: 1 }}
            color="primary"
            onClick={() => navigate(ROUTES.BRIDGE_HISTORY)}
          >
            <MdHistory />
          </IconButton>
        }
      />

      <Box borderBottom={1} borderColor="divider" mb={2} mt={0.75} mx={2.5}>
        <Tabs
          value={bridgeMode === 'fromICP' ? 0 : 1}
          onChange={(_e, index) => setBridgeMode(index === 0 ? 'fromICP' : 'toICP')}
        >
          <Tab label={t('onesec.fromIcp')} sx={{ width: '50%' }} />
          <Tab label={t('onesec.toIcp')} sx={{ width: '50%' }} />
        </Tabs>
      </Box>

      <Stack mx={2} spacing={1.25}>
        <TokenCard
          token={token}
          amount={amount}
          setAmount={setAmount}
          mode="stake"
          error={undefined}
          showBalance
          maxAmount={maxAmount}
          tokensForSelect={fromTokens}
          onTokenSelect={setToken}
        />

        <List component={Paper} disablePadding>
          <BridgeListItem attribute={t('onesec.toChain')}>
            <Touchable onClick={() => setChainSelectVisible(true)}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <img
                  src={getChainIcon(toChain)}
                  width={25}
                  height={25}
                  style={{ marginRight: 2 }}
                />
                <Typography fontWeight="medium" color="text.primary">
                  {t(`chains.long.${toChain}`)}
                </Typography>
                <HiChevronDown color="gray" size={20} />
              </Stack>
            </Touchable>
          </BridgeListItem>

          <BridgeListItem attribute={t('onesec.receiveAmount')}>
            {formatNumber(receiveAmount)} {token?.symbol}
          </BridgeListItem>

          <BridgeListItem attribute={t('onesec.commision')}>
            {fee ? (
              <>
                {formatNumber(fee?.commision)} {token?.symbol} = {formatMoney(fee.commisionMoney)}
              </>
            ) : (
              'Loading'
            )}
          </BridgeListItem>

          {bridgeMode === 'toICP' && (
            <BridgeListItem attribute={t('onesec.gasFee')}>
              {fee ? (
                <>
                  {formatNumber(fee.gasFee, 'ETH')} = {formatMoney(fee.gasMoney)}
                </>
              ) : (
                'Loading'
              )}
            </BridgeListItem>
          )}

          {bridgeMode === 'toICP' && (
            <BridgeListItem attribute={t('onesec.ethBalance')}>
              {formatNumber(ethBalance?.balance)} ETH
            </BridgeListItem>
          )}

          <Divider sx={{ borderColor: 'background.default' }} />
        </List>
        <Button
          variant="rainbow"
          disabled={!!error || bridgeInProgress}
          onClick={() => setBridgeReviewVisible(true)}
          fullWidth
        >
          {error || t('onesec.reviewBridge')}
        </Button>
      </Stack>

      <ChainPicker
        chains={bridgeMode === 'fromICP' ? ETH_SUPPORTED_CHAINS : [Chain.ICP]}
        initialChain={toChain}
        chainSelectVisible={chainSelectVisible}
        setChainSelectVisible={setChainSelectVisible}
        onChainChange={(chain) => setToChain(chain as Chain)}
      />

      <Prompt show={!!prompt} close={() => setPrompt(null)}>
        <Stack alignItems="center">
          <Stack alignItems="center" mb={0.5}>
            <Typography variant="h6" mt={0.2}>
              {prompt?.title}
            </Typography>
            <Typography
              textAlign="center"
              mt={1}
              mb={2}
              color="text.light"
              sx={{ whiteSpace: 'pre-line' }}
            >
              {prompt?.description}
            </Typography>
          </Stack>

          <Button onClick={() => setPrompt(null)} variant="outlined">
            {t('common.understood')}
          </Button>
        </Stack>
      </Prompt>

      <BottomSheet
        visible={bridgeReviewVisible}
        onBackdropClick={() => setBridgeReviewVisible(false)}
      >
        <ReviewMint
          fromToken={token}
          toToken={toToken}
          amount={amount}
          receiveAmount={receiveAmount.toString()}
          loading={bridgeInProgress}
          performMint={bridge}
          mintSuccess={success}
          step={bridgeStep}
          kind="bridge"
        />
      </BottomSheet>
    </Stack>
  )
}

export default BridgeOneSec
